import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, CheckCircle, Info, Warning, XCircle, Trash, EnvelopeOpen, ArrowSquareOut } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICON = {
  info:    { Icon: Info, cls: 'text-sky-600 bg-sky-50 dark:text-sky-300 dark:bg-sky-950/50' },
  success: { Icon: CheckCircle, cls: 'text-emerald-600 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/50' },
  warning: { Icon: Warning, cls: 'text-amber-600 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/50' },
  error:   { Icon: XCircle, cls: 'text-red-600 bg-red-50 dark:text-red-300 dark:bg-red-950/50' },
};

const POLL_INTERVAL_MS = 30_000;

/**
 * NotificationBell — top-bar bell with red unread badge.
 * Polls /api/in-app-notifications/unread-count every 30s.
 * Click → popover with last 50 notifications, click row → mark read & navigate
 * to its `link` (if any).
 */
export default function NotificationBell() {
  const navigate = useNavigate();
  const { language, isRTL } = useLanguage();
  const { user } = useAuth();
  // Employees see notifications in their preferred notification_language;
  // admins fall back to UI language.
  const contentLang = (user?.role === 'employee' && (user?.notification_language === 'ar' || user?.notification_language === 'en'))
    ? user.notification_language
    : language;
  const isAr = contentLang === 'ar';

  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Detail dialog — opened whenever the user clicks a notification so
  // the full title/body (which are truncated in the popover preview)
  // are always readable.
  const [selected, setSelected] = useState(null);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/in-app-notifications/unread-count`, { withCredentials: true });
      setUnread(data.count || 0);
    } catch {
      // silent — auth may be expired or service down
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/in-app-notifications`, { withCredentials: true });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const handleClickItem = async (n) => {
    if (!n.read) {
      try {
        await axios.post(`${API}/in-app-notifications/${n.id}/read`, {}, { withCredentials: true });
        setItems((p) => p.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* silent — notification read fires optimistically */ }
    }
    // Always open the detail dialog so the user can read the full text.
    // The "Open link" button inside the dialog handles navigation when
    // the notification has one — this way the user is never left
    // wondering what a truncated title actually said.
    setOpen(false);
    setSelected({ ...n, read: true });
  };

  const handleFollowLink = () => {
    if (selected?.link) {
      const link = selected.link;
      setSelected(null);
      navigate(link);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/in-app-notifications/read-all`, {}, { withCredentials: true });
      setItems((p) => p.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch { /* silent — mark-all is best effort */ }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/in-app-notifications/${id}`, { withCredentials: true });
      setItems((p) => p.filter((x) => x.id !== id));
      const removed = items.find((x) => x.id === id);
      if (removed && !removed.read) setUnread((u) => Math.max(0, u - 1));
    } catch { /* silent — the row simply stays visible */ }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const dt = new Date(iso);
    const diffMs = Date.now() - dt.getTime();
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return isAr ? 'الآن' : 'now';
    if (minutes < 60) return isAr ? `منذ ${minutes} دقيقة` : `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return isAr ? `منذ ${hours} ساعة` : `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return isAr ? `منذ ${days} يوم` : `${days}d ago`;
    return dt.toLocaleDateString(isAr ? 'ar-SA' : 'en-US');
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-[#655dc6] hover:bg-[#E8E6F5]"
          data-testid="notification-bell"
        >
          <Bell size={22} weight="duotone" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="bell-unread-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[360px] p-0 border-[#C4BFF0] dark:border-slate-700 dark:bg-slate-900"
        align={isRTL ? 'start' : 'end'}
        dir={isRTL ? 'rtl' : 'ltr'}
        data-testid="bell-popover"
      >
        <div className="px-3 py-2 border-b border-[#C4BFF0]/50 dark:border-slate-700/60 flex items-center justify-between bg-[#F2F0FA] dark:bg-slate-800/70">
          <p className="text-sm font-semibold text-[#655dc6] dark:text-[#a49aff] flex items-center gap-1">
            <Bell size={16} weight="duotone" />
            {isAr ? 'الإشعارات' : 'Notifications'}
            {unread > 0 && (
              <span className="text-[10px] font-normal bg-[#655dc6] text-white px-1.5 py-0.5 rounded-full">
                {unread}
              </span>
            )}
          </p>
          {unread > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-[10px] text-[#655dc6] dark:text-[#a49aff] hover:text-[#544cb0] dark:hover:text-white flex items-center gap-1"
              data-testid="mark-all-read"
            >
              <EnvelopeOpen size={12} />
              {isAr ? 'تعليم الكل كمقروء' : 'Mark all read'}
            </button>
          )}
        </div>

        {/* Native overflow-y so the scrollbar is ALWAYS visible when the list
            exceeds 420px — the Radix ScrollArea previously hid its handle
            until hover, which made users think there were no more items. */}
        <div
          className="max-h-[420px] overflow-y-auto notif-scroll"
          dir={isRTL ? 'rtl' : 'ltr'}
          data-testid="bell-scroll"
        >
          {loading ? (
            <div className="py-6 text-center text-xs text-[#655dc6]/60 dark:text-slate-400">
              {isAr ? 'جاري التحميل...' : 'Loading...'}
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-[#655dc6]/60 dark:text-slate-400">
              <Bell size={36} weight="duotone" className="mx-auto mb-2 opacity-40" />
              {isAr ? 'لا توجد إشعارات' : 'No notifications yet'}
            </div>
          ) : (
            <ul className="divide-y divide-[#C4BFF0]/40 dark:divide-slate-700/60">
              {items.map((n) => {
                const meta = TYPE_ICON[n.type] || TYPE_ICON.info;
                const Icon = meta.Icon;
                const title = isAr ? (n.title_ar || n.title_en) : (n.title_en || n.title_ar);
                const body = isAr ? (n.body_ar || n.body_en) : (n.body_en || n.body_ar);
                return (
                  <li
                    key={n.id}
                    onClick={() => handleClickItem(n)}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className={`group flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-[#F8F7FB] dark:hover:bg-slate-800/60 ${!n.read ? 'bg-[#F2F0FA]/60 dark:bg-slate-800/40' : ''}`}
                    data-testid={`bell-item-${n.id}`}
                  >
                    {/* icon + text stay together so the icon sits next to the title in both RTL and LTR */}
                    <div className="flex-1 min-w-0 flex items-start gap-2">
                      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${meta.cls}`}>
                        <Icon size={16} weight="duotone" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm line-clamp-2 ${!n.read ? 'font-bold text-[#655dc6] dark:text-[#c9c1ff]' : 'font-medium text-[#655dc6]/80 dark:text-slate-300'}`}>
                            {title}
                          </p>
                          {!n.read && <span className="shrink-0 w-2 h-2 rounded-full bg-[#655dc6] dark:bg-[#a49aff] mt-1.5" />}
                        </div>
                        {body && (
                          <p className="text-[11px] text-[#655dc6]/70 dark:text-slate-400 mt-0.5 line-clamp-2">{body}</p>
                        )}
                        <p className="text-[10px] text-[#655dc6]/50 dark:text-slate-500 mt-1">{formatTime(n.created_at)}</p>
                      </div>
                    </div>
                    {/* trash always on the opposite side */}
                    <button
                      onClick={(e) => handleDelete(e, n.id)}
                      className="opacity-0 group-hover:opacity-100 transition text-[#655dc6]/40 dark:text-slate-500 hover:text-red-500"
                      data-testid={`bell-delete-${n.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>

      {/* Notification detail dialog — shows the FULL title + body so
          long announcements (e.g. HR company-wide messages) are never
          truncated behind an ellipsis.  It lives OUTSIDE the Popover
          so it survives the popover close on click. */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent
          dir={isRTL ? 'rtl' : 'ltr'}
          className="sm:max-w-[560px]"
          data-testid="notification-detail-dialog"
        >
          {selected && (() => {
            const meta = TYPE_ICON[selected.type] || TYPE_ICON.info;
            const DIcon = meta.Icon;
            const title = isAr ? (selected.title_ar || selected.title_en) : (selected.title_en || selected.title_ar);
            const body = isAr ? (selected.body_ar || selected.body_en) : (selected.body_en || selected.body_ar);
            return (
              <div dir={isRTL ? 'rtl' : 'ltr'} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                <DialogHeader>
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${meta.cls}`}>
                      <DIcon size={22} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <DialogTitle
                        className="text-[#655dc6] dark:text-[#c9c1ff] text-start leading-snug"
                        data-testid="detail-title"
                      >
                        {title}
                      </DialogTitle>
                      <DialogDescription className="text-[10px] text-[#655dc6]/60 dark:text-slate-400 mt-0.5">
                        {formatTime(selected.created_at)}
                      </DialogDescription>
                    </div>
                  </div>
                </DialogHeader>

                <div
                  className="mt-4 text-sm text-[#2D2A5C] dark:text-slate-200 whitespace-pre-wrap break-words leading-relaxed max-h-[55vh] overflow-y-auto"
                  data-testid="detail-body"
                >
                  {body || (isAr ? 'لا توجد تفاصيل إضافية.' : 'No additional details.')}
                </div>

                <div className="mt-6 flex flex-wrap justify-end gap-2">
                  {selected.link && (
                    <Button
                      onClick={handleFollowLink}
                      className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2"
                      data-testid="detail-open-link"
                    >
                      <ArrowSquareOut size={14} />
                      {isAr ? 'فتح' : 'Open'}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setSelected(null)}
                    className="border-[#C4BFF0] dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                    data-testid="detail-close"
                  >
                    {isAr ? 'إغلاق' : 'Close'}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
}
